// src/utils/successionDossierPdf.js
// Succession Dossier (§4 TRANSMIT, §12 notaires) — document préparatoire de transmission
// pour une collection. Inventaire + détention (pleine propriété / usufruit / nue-propriété)
// + attribution par bénéficiaire (source : artwork_beneficiary) + œuvres exceptionnelles.
// ARTKIV prépare le dossier ; le notaire/les professionnels habilités valident (§12/§13).
// jsPDF v3 : import jsPDF from "jspdf" + autoTable(doc, {...}).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { BRAND_UPPER } from "../lib/rebrand";

const OWNERSHIP = { full: "Pleine propriété", usufruct: "Usufruit", bare_ownership: "Nue-propriété" };
const eur = (v) => (v || v === 0 ? `${Number(v).toLocaleString("fr-FR")} €` : "—");
const currentValue = (a) =>
  Number(a?.current_estimated_value ?? a?.valuation_estimated_value ?? a?.estimated_value ?? a?.value ?? 0) || 0;

// benByArtwork : { [artwork_id]: [{ name, relation }] }
export function generateSuccessionDossier({ collectionName = "", ownerName = "", artworks = [], benByArtwork = {} } = {}) {
  const doc = new jsPDF("p", "mm", "a4");
  const M = 16;
  let y = 18;

  const total = artworks.length;
  const totalValue = artworks.reduce((s, a) => s + currentValue(a), 0);
  const exceptional = artworks.filter((a) => a?.is_exceptional === true);
  const withBen = artworks.filter((a) => (benByArtwork[a.id] || []).length > 0);

  // ---- En-tête ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(150);
  doc.text("DOSSIER DE SUCCESSION — DOCUMENT PRÉPARATOIRE", M, y);
  doc.setFontSize(18); doc.setTextColor(20);
  y += 9; doc.text(doc.splitTextToSize(collectionName || "Collection", 180), M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
  y += 6; if (ownerName) { doc.text(`Titulaire : ${ownerName}`, M, y); y += 5; }

  // ---- Synthèse ----
  autoTable(doc, {
    startY: y + 2, theme: "plain", margin: { left: M, right: M },
    styles: { fontSize: 10, cellPadding: 1.6 },
    columnStyles: { 0: { fontStyle: "bold", textColor: 120, cellWidth: 70 } },
    body: [
      ["Nombre d'œuvres", String(total)],
      ["Valeur totale estimée", eur(totalValue)],
      ["Œuvres exceptionnelles", String(exceptional.length)],
      ["Œuvres avec bénéficiaire désigné", `${withBen.length} / ${total}`],
    ],
  });
  y = (doc.lastAutoTable?.finalY || y) + 8;

  // ---- Inventaire & attribution ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
  doc.text("Inventaire & attribution", M, y);
  autoTable(doc, {
    startY: y + 3, margin: { left: M, right: M },
    head: [["Œuvre", "Artiste", "Valeur", "Détention", "Bénéficiaire(s)"]],
    body: artworks
      .slice()
      .sort((a, b) => currentValue(b) - currentValue(a))
      .map((a) => [
        (a.is_exceptional ? "★ " : "") + (a.title || "(sans titre)"),
        a.artist || "—",
        eur(currentValue(a)),
        OWNERSHIP[a.ownership_status] || a.ownership_status || "—",
        (benByArtwork[a.id] || []).map((b) => b.name + (b.relation ? ` (${b.relation})` : "")).join(", ") || "—",
      ]),
    headStyles: { fillColor: [26, 26, 26], fontSize: 8.5 },
    styles: { fontSize: 8.5, cellPadding: 1.6 },
    columnStyles: { 2: { halign: "right" } },
  });
  y = (doc.lastAutoTable?.finalY || y) + 8;

  // ---- Répartition par bénéficiaire ----
  const byBen = new Map(); // name -> { relation, count, value }
  let unassignedCount = 0, unassignedValue = 0;
  for (const a of artworks) {
    const bens = benByArtwork[a.id] || [];
    if (bens.length === 0) { unassignedCount += 1; unassignedValue += currentValue(a); continue; }
    for (const b of bens) {
      const cur = byBen.get(b.name) || { relation: b.relation || "", count: 0, value: 0 };
      cur.count += 1; cur.value += currentValue(a); // valeur non divisée si co-attribution
      byBen.set(b.name, cur);
    }
  }
  const rows = [...byBen.entries()].map(([name, v]) => [
    name + (v.relation ? ` (${v.relation})` : ""),
    String(v.count),
    eur(v.value),
    totalValue ? `${Math.round((v.value / totalValue) * 100)} %` : "—",
  ]);
  if (unassignedCount) rows.push(["Non attribué", String(unassignedCount), eur(unassignedValue), totalValue ? `${Math.round((unassignedValue / totalValue) * 100)} %` : "—"]);

  if (y > 245) { doc.addPage(); y = 18; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
  doc.text("Répartition par bénéficiaire", M, y);
  autoTable(doc, {
    startY: y + 3, margin: { left: M, right: M },
    head: [["Bénéficiaire", "Œuvres", "Valeur attribuée", "Part"]],
    body: rows.length ? rows : [["—", "0", "—", "—"]],
    headStyles: { fillColor: [26, 26, 26], fontSize: 9 },
    styles: { fontSize: 9, cellPadding: 1.8 },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
  });
  y = (doc.lastAutoTable?.finalY || y) + 3;
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  doc.text("★ œuvre exceptionnelle. En cas de co-attribution, la valeur n'est pas divisée automatiquement.", M, y + 3);

  // ---- Pied notaire (§12/§13) ----
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  const foot =
    "Document préparatoire établi via " + BRAND_UPPER + " le " + new Date().toLocaleDateString("fr-FR") + ". " +
    "Il ne se substitue ni à l'acte notarié, ni à l'expertise, ni au conseil fiscal. " + BRAND_UPPER + " structure la donnée ; " +
    "le notaire et les professionnels habilités valident ce qui relève de leur compétence.";
  doc.text(doc.splitTextToSize(foot, 180), M, 290);

  const slug = (collectionName || "collection").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  doc.save(`dossier-succession-${slug || "collection"}.pdf`);
}
